import Card from 'antd/lib/card';
import Empty from 'antd/lib/empty';
import * as echarts from 'echarts/core';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { LineChart } from 'echarts/charts';
import { DataZoomComponent, GridComponent, LegendComponent, MarkAreaComponent, MarkLineComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { EChartsOption } from 'echarts';
import { TopLevelFormatterParams } from 'echarts/types/dist/shared';
import { CHART_DATA_COLOR } from '../../../peak-load-forecast/Charts/chart';
import { PriceType } from '../../../../domain/price-response/interface';
import { PriceResponseMarket } from './PriceResponseMarket';
import { IPricePoint } from './calculatePriceResponseScenariosEvents';
import { getTimezoneFormattedTime } from '../../../../domain/date/date';

echarts.use([LineChart, TooltipComponent, LegendComponent, GridComponent, CanvasRenderer, MarkAreaComponent, MarkLineComponent, DataZoomComponent]);

interface IPriceResponseScenariosChartProps {
    series: IPricePoint[];
    triggerPrice: number;
    priceType: PriceType;
    market: PriceResponseMarket;
    isLoading: boolean;
}

const seriesName = (priceType: PriceType) => (priceType === PriceType.REAL_TIME ? 'Real Time Price' : 'Day Ahead Price');

const formatTooltip = (params: TopLevelFormatterParams, priceType: PriceType, market: PriceResponseMarket): string => {
    if (!Array.isArray(params) || !params.length) return '';

    const point = params.find(p => p.seriesName === seriesName(priceType)) ?? params[0];
    const [timestamp, price] = point.value as [number, number];

    const dateStr = getTimezoneFormattedTime(new Date(timestamp), market.timezone);
    const formattedPrice = typeof price === 'number' ? `$${price.toFixed(2)}` : 'N/A';

    return `<div style="padding: 6px 10px;">
        <div style="color: #697475; margin-bottom: 4px;">${dateStr}</div>
        <div>${point.marker} ${point.seriesName}: <strong>${formattedPrice}</strong></div>
    </div>`;
};

export const PriceResponseScenariosPriceChart = ({ series, triggerPrice, priceType, market, isLoading }: IPriceResponseScenariosChartProps) => {
    const chartData = series.map(point => [point.timestamp, point.price]);
    const name = seriesName(priceType);

    const isNoData = chartData.length === 0;

    const values = chartData.map(([, value]) => value).filter((value): value is number => value != null);

    const maxValue = Math.ceil(Math.max(...values, triggerPrice));

    const option: EChartsOption = {
        grid: {
            top: '40px',
            left: '80px',
            right: '40px',
            bottom: '60px',
        },
        legend: {
            top: 0,
            selectedMode: false,
        },
        tooltip: {
            trigger: 'axis',
            padding: 0,
            borderColor: '#ffffff',
            axisPointer: {
                type: 'line',
                lineStyle: { type: 'dashed' },
            },
            formatter: params => formatTooltip(params, priceType, market),
        },
        xAxis: {
            type: 'time',
            axisLabel: {
                color: '#697475',
                formatter: (value: number) => getTimezoneFormattedTime(new Date(value), market.timezone),
                hideOverlap: true,
            },
            axisLine: {
                lineStyle: { color: '#d6dbdb', width: 2 },
            },
        },
        yAxis: {
            type: 'value',
            max: maxValue,
            axisLabel: {
                color: '#697475',
                formatter: (value: number) => `$${value}`,
            },
            splitLine: { show: true },
            axisLine: {
                lineStyle: { color: '#d6dbdb', width: 2 },
            },
        },
        dataZoom: [
            {
                type: 'inside',
            },
            {
                type: 'slider',
                height: 20,
                bottom: 10,
                labelFormatter: value => getTimezoneFormattedTime(new Date(value), market.timezone),
            },
        ],
        series: [
            {
                name,
                type: 'line',
                data: chartData,
                showSymbol: false,
                smooth: false,
                sampling: 'lttb',
                color: CHART_DATA_COLOR,
                lineStyle: { width: 2, color: CHART_DATA_COLOR },
                areaStyle: { color: 'rgba(116, 215, 213, 0.35)' },
                markLine: {
                    silent: false,
                    symbol: 'none',
                    lineStyle: {
                        color: '#ff4d4f',
                        type: 'dashed',
                        width: 2,
                    },
                    label: {
                        formatter: `Trigger $${triggerPrice}`,
                        position: 'insideEndTop',
                        color: '#ff4d4f',
                    },
                    data: [{ yAxis: triggerPrice, name: 'Trigger' }],
                },
            },
        ],
    };

    return (
        <Card title="Prices" style={{ marginBottom: 16 }}>
            {isNoData ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
                <ReactEChartsCore
                    echarts={echarts}
                    option={option}
                    style={{ height: 400, width: '100%' }}
                    showLoading={isLoading}
                    loadingOption={{ color: 'rgba(116, 215, 213, 0.8)' }}
                    notMerge
                    lazyUpdate
                />
            )}
        </Card>
    );
};
